/**
 * WmsTaskAssignPanel
 * ------------------
 * Feb 2026 — the per-order Warehouse Task Assign panel that sits next
 * to the Driver panel on each expanded order card in Admin Orders.
 *
 * Renders three rows (Picking / Loading / Put-away). Each row shows:
 *   • A colored status pill: "None" | "Pending" | "In Progress" |
 *     "Completed" | "Mixed"
 *   • An assignee dropdown listing every active user with any
 *     warehouse role. Blank option = unassign.
 *   • A small counter chip "X/Y done" beside the pill when total>0.
 *
 * Gated by `orders.assign_wms_tasks` (super admin always). Independent
 * of the driver panel — a manager can hold either or both permissions.
 *
 * Powered by:
 *   GET  /api/admin/warehouse/tasks/summary-for-order/{id}
 *   POST /api/admin/warehouse/tasks/bulk-assign-by-order-and-type
 *   GET  /api/admin/warehouse/tasks/assignable-users
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ClipboardCheck, Truck as TruckIcon, Boxes, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import MultiSplitAssignModal from './MultiSplitAssignModal';

const STATUS_PILL = {
  none:        { label: 'None',        cls: 'bg-neutral-100 text-neutral-500 border border-neutral-200' },
  pending:     { label: 'Pending',     cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  in_progress: { label: 'In progress', cls: 'bg-sky-100 text-sky-800 border border-sky-200' },
  completed:   { label: 'Completed',   cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
};

const TYPES = [
  { key: 'picking', label: 'Picking',   Icon: ClipboardCheck },
  { key: 'loading', label: 'Loading',   Icon: TruckIcon },
  { key: 'putaway', label: 'Put-away',  Icon: Boxes },
];

const WmsTaskAssignPanel = ({ orderId, API, authHeaders, canAssign }) => {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState(''); // 'picking' | 'loading' | 'putaway'
  // Feb 2026 — multi-picker split modal state. Scoped to picking +
  // put-away only (loading is a single-doc task so a split would be
  // meaningless). The modal is reused as-is; the panel just opens it
  // with the right `type` arg.
  const [splitType, setSplitType] = useState(null); // 'picking' | 'putaway' | null
  const { toast } = useToast();

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        axios.get(`${API}/admin/warehouse/tasks/summary-for-order/${orderId}`, { headers: authHeaders }),
        canAssign
          ? axios.get(`${API}/admin/warehouse/tasks/assignable-users`, { headers: authHeaders })
          : Promise.resolve({ data: { users: [] } }),
      ]);
      setSummary(s.data);
      setUsers(u.data?.users || []);
    } catch (e) {
      // Non-fatal — the panel just stays in an idle state.
      console.error('WmsTaskAssignPanel load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // Feb 2026 — silently re-poll the SUMMARY every 30 s while the
    // panel is mounted so completion counts (X/Y done + status pill)
    // update without needing to collapse/expand the order card.
    // Deliberately does NOT re-fetch the assignable-users list so
    // the dropdown doesn't flicker while the user is picking.
    const iv = setInterval(async () => {
      try {
        const s = await axios.get(
          `${API}/admin/warehouse/tasks/summary-for-order/${orderId}`,
          { headers: authHeaders },
        );
        setSummary(s.data);
      } catch (_e) { /* transient network — try again next tick */ }
    }, 30_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      // Managers first, then floor_incharge, then everyone else alpha.
      const rank = { manager: 0, floor_incharge: 1, picker: 2, viewer: 3 };
      const ra = rank[a.role] ?? 9;
      const rb = rank[b.role] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  }, [users]);

  const changeAssignee = async (type, nextEmail) => {
    setSavingType(type);
    try {
      const r = await axios.post(
        `${API}/admin/warehouse/tasks/bulk-assign-by-order-and-type`,
        { order_id: orderId, type, assignee_email: nextEmail || '' },
        { headers: authHeaders },
      );
      const updated = r.data?.updated || 0;
      const intentStored = !!r.data?.intent_stored;
      if (updated === 0 && intentStored && nextEmail) {
        // Feb 2026 — with loading tasks now spawned at picking-time
        // this branch only fires when the order hasn't been
        // allocated yet (no picking tasks → no loading task). Reflect
        // that the pick was captured as an intent that will apply the
        // moment the task actually spawns, instead of a "no tasks"
        // toast which read like the save had failed.
        toast({
          title: 'Pre-assigned',
          description: `Pre-assigned, will apply when ${type} starts.`,
        });
      } else if (updated === 0) {
        toast({
          title: `No ${type} tasks`,
          description: `This order has no ${type} tasks to assign yet.`,
        });
      } else {
        toast({
          title: nextEmail ? 'Assigned' : 'Cleared',
          description: `${updated} ${type} task${updated === 1 ? '' : 's'}.`,
        });
      }
      await load();
    } catch (err) {
      toast({
        title: 'Assign failed',
        description: err.response?.data?.detail || 'Try again.',
      });
    } finally {
      setSavingType('');
    }
  };

  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-white p-4"
      data-testid={`wms-task-panel-${orderId}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          WMS task assignment
        </div>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />}
      </div>
      <div className="space-y-2">
        {TYPES.map(({ key, label, Icon }) => {
          const row = summary?.types?.[key] || {
            status: 'none', total: 0, completed: 0, mixed: false,
            assignee_email: null, assignee_name: null,
          };
          const pill = STATUS_PILL[row.status] || STATUS_PILL.none;
          return (
            <React.Fragment key={key}>
              <div
                className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-neutral-50 border border-neutral-100"
                data-testid={`wms-task-row-${orderId}-${key}`}
              >
              <div className="flex items-center gap-2 min-w-[110px]">
                <Icon className="w-4 h-4 text-neutral-500" />
                <span className="text-sm font-semibold text-[#0F172A]">{label}</span>
              </div>
              <span
                className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${pill.cls}`}
                data-testid={`wms-task-pill-${orderId}-${key}`}
              >
                {pill.label}
              </span>
              {row.total > 0 && (
                <span className="text-[11px] text-neutral-500">
                  {row.completed}/{row.total} done
                </span>
              )}
              {row.mixed && (
                <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-200">
                  Mixed
                </span>
              )}
              {canAssign ? (
                <>
                  <select
                    value={row.assignee_email || ''}
                    disabled={savingType === key}
                    onChange={(e) => changeAssignee(key, e.target.value)}
                    className="ml-auto max-w-[220px] rounded-lg border border-neutral-300 px-2 py-1 text-xs focus:border-[#0078D4] focus:outline-none disabled:opacity-60"
                    data-testid={`wms-task-assignee-${orderId}-${key}`}
                  >
                    <option value="">
                      {row.mixed
                        ? '— mixed, pick one —'
                        : row.total === 0
                          ? (row.intent_only ? '— pre-assigned —' : '— Pre-assign someone —')
                          : '— Unassigned —'}
                    </option>
                    {sortedUsers.map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.name || u.email} · {u.role || '—'}
                      </option>
                    ))}
                  </select>
                  {/* Feb 2026 — Multi-split: open the shared modal
                      scoped to this (order, type). Only shown for
                      picking + put-away since loading is a single
                      task. */}
                  {(key === 'picking' || key === 'putaway') && row.total > 0 && (
                    <button
                      type="button"
                      onClick={() => setSplitType(key)}
                      className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 font-semibold px-2 py-1 rounded-full border border-sky-200 bg-sky-50"
                      data-testid={`wms-task-split-${orderId}-${key}`}
                      title="Split tasks across multiple pickers"
                    >
                      <Users className="w-3 h-3" /> Split
                    </button>
                  )}
                </>
              ) : (
                <span className="ml-auto text-xs text-neutral-500 italic">
                  {row.assignee_name || (row.total === 0 ? '—' : 'Unassigned')}
                </span>
              )}
            </div>
            {row.intent_only && row.assignee_email && (
              <div className="text-[10px] text-neutral-500 pl-6 -mt-1">
                Pre-assigned to <b>{row.assignee_name || row.assignee_email}</b>. Will apply automatically when {key} tasks are created.
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>
      {!canAssign && (
        <div className="mt-2 text-[11px] text-neutral-500 italic">
          Read-only — request the &quot;Assign WMS tasks per order&quot; permission to change assignees.
        </div>
      )}
      {splitType && (
        <MultiSplitAssignModal
          open={!!splitType}
          onClose={() => setSplitType(null)}
          scope="order"
          orderId={orderId}
          type={splitType}
          users={users}
          API={API}
          authHeaders={authHeaders}
          onAssigned={load}
        />
      )}
    </div>
  );
};

export default WmsTaskAssignPanel;
